import React, { useState } from 'react';
import {
  gridFromResult, combineGrids, gridToResultGeoJSON,
  gridVolume, gridResiduals, blankGridOutside,
} from '../lib/grid-ops';
import { useTheme } from '../contexts/ThemeContext';

const OPERATIONS = [
  { id: 'subtract', label: 'Subtract (Z1 − Z2)', fn: (a, b) => a - b },
  { id: 'ratio', label: 'Ratio (Z1 ÷ Z2)', fn: (a, b) => b === 0 ? NaN : a / b },
  { id: 'sum', label: 'Sum (Z1 + Z2)', fn: (a, b) => a + b },
  { id: 'product', label: 'Product (Z1 × Z2)', fn: (a, b) => a * b },
  { id: 'pctchange', label: '% Change ((Z1−Z2)/Z2·100)', fn: (a, b) => b === 0 ? NaN : ((a - b) / b) * 100 },
  { id: 'formula', label: 'Custom Formula…', fn: null },
];

/** Compile a Z1/Z2 formula like "(Z1 - Z2) / Z1 * 100" into a safe function. */
function compileFormula(expr) {
  const cleaned = expr.trim();
  // Whitelist: numbers, operators, parens, whitespace, Z1/Z2 tokens, Math functions we allow
  const stripped = cleaned
    .replace(/Z1|Z2/g, '')
    .replace(/\b(abs|sqrt|log|log10|exp|pow|min|max)\s*\(/g, '(');
  if (!/^[\d+\-*/().,\s]*$/.test(stripped)) {
    throw new Error('Formula may only use Z1, Z2, numbers, + − × ÷ ( ), and abs/sqrt/log/log10/exp/pow/min/max');
  }
  const body = cleaned.replace(/\b(abs|sqrt|log|log10|exp|pow|min|max)\b/g, m => `Math.${m}`);
  const fn = new Function('Z1', 'Z2', `"use strict"; return (${body});`);
  // Probe
  const test = fn(1, 2);
  if (typeof test !== 'number') throw new Error('Formula must return a number');
  return fn;
}

const MODES = [
  { id: 'zfields', label: 'Z Fields' },
  { id: 'grids', label: 'Grids' },
  { id: 'volume', label: 'Volume' },
  { id: 'residuals', label: 'Residuals' },
  { id: 'blanking', label: 'Blanking' },
];

/**
 * GridCombinePanel — Surfer-style grid operations
 *
 * Modes: Z-field math, grid↔grid math, volume above/below datum,
 * data-minus-grid residuals, blanking outside a polygon boundary.
 *
 * Props:
 *   layers      - imported XYZ layers [{ id, name, xyz: { zFields }, geojson }]
 *   gridLayers  - gridding result layers [{ id, name, geojson (with metadata.grid) }]
 *   drawnPolygons - GeoJSON Polygon features (from Terra Draw) for blanking
 *   onDerive(layerId, fieldName, computeFn) - add a derived Z field
 *   onAddResultLayer(geojson, name, kind)   - add a new grid/point result layer
 *   onClose()
 */
export default function GridCombinePanel({ layers = [], gridLayers = [], drawnPolygons = [], onDerive, onAddResultLayer, onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [mode, setMode] = useState('zfields');
  const [gridA, setGridA] = useState('');
  const [gridB, setGridB] = useState('');
  const [gridOp, setGridOp] = useState('subtract');
  const [datum, setDatum] = useState('0');
  const [volResult, setVolResult] = useState(null);
  const [resLayerId, setResLayerId] = useState('');
  const [resAttr, setResAttr] = useState('');
  const xyzLayers = layers.filter(l => l.xyz?.zFields?.length >= 1);
  const [layerId, setLayerId] = useState(xyzLayers[0]?.id || '');
  const [z1, setZ1] = useState('');
  const [z2, setZ2] = useState('');
  const [op, setOp] = useState('subtract');
  const [formula, setFormula] = useState('(Z1 - Z2) / Z1 * 100');
  const [outName, setOutName] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const layer = xyzLayers.find(l => l.id === layerId);
  const zFields = layer?.xyz?.zFields || [];

  const run = () => {
    setError(null);
    setSuccess(null);
    try {
      if (!layer) throw new Error('Select an XYZ layer');
      if (!z1 || !z2) throw new Error('Select both Z1 and Z2 fields');

      let fn;
      const operation = OPERATIONS.find(o => o.id === op);
      if (op === 'formula') {
        fn = compileFormula(formula);
      } else {
        fn = operation.fn;
      }

      const name = outName.trim() || (op === 'formula' ? 'derived' : `${z1}_${op}_${z2}`);
      if (zFields.includes(name)) throw new Error(`Field "${name}" already exists — pick another name`);

      let computed = 0;
      const compute = (props) => {
        const a = parseFloat(props?.[z1]);
        const b = parseFloat(props?.[z2]);
        if (isNaN(a) || isNaN(b)) return null;
        const v = fn(a, b);
        if (typeof v !== 'number' || !isFinite(v)) return null;
        computed++;
        return Math.round(v * 1e6) / 1e6;
      };

      onDerive(layerId, name, compute);
      setSuccess(`✓ Derived field "${name}" added${computed ? '' : ''} — select it in the Z panel to plot`);
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Grid-mode handlers ──
  const usableGrids = gridLayers.filter(l => gridFromResult(l.geojson));

  const runGridMath = () => {
    setError(null); setSuccess(null);
    try {
      const a = usableGrids.find(l => l.id === gridA);
      const b = usableGrids.find(l => l.id === gridB);
      if (!a || !b) throw new Error('Select two grid layers');
      let fn;
      const operation = OPERATIONS.find(o => o.id === gridOp);
      fn = gridOp === 'formula' ? compileFormula(formula) : operation.fn;
      const combined = combineGrids(gridFromResult(a.geojson), gridFromResult(b.geojson), fn);
      const geojson = gridToResultGeoJSON(combined.grid, combined.bbox);
      if (geojson.features.length === 0) throw new Error('Result grid is empty (no overlapping cells)');
      onAddResultLayer(geojson, `Grid ${operation?.label || gridOp}: ${a.name} ~ ${b.name}`, 'grid');
      setSuccess(`✓ Combined grid added (${geojson.features.length} cells)`);
    } catch (err) { setError(err.message); }
  };

  const runVolume = () => {
    setError(null); setSuccess(null); setVolResult(null);
    try {
      const a = usableGrids.find(l => l.id === gridA);
      if (!a) throw new Error('Select a grid layer');
      const g = gridFromResult(a.geojson);
      const v = gridVolume(g.grid, g.bbox, parseFloat(datum) || 0);
      setVolResult(v);
    } catch (err) { setError(err.message); }
  };

  const runResiduals = () => {
    setError(null); setSuccess(null);
    try {
      const a = usableGrids.find(l => l.id === gridA);
      const src = xyzLayers.find(l => l.id === resLayerId);
      if (!a) throw new Error('Select a grid layer');
      if (!src || !resAttr) throw new Error('Select an XYZ layer and value attribute');
      const points = src.geojson.features
        .filter(f => f.geometry?.type === 'Point' && !f.properties?._excluded)
        .map(f => ({ lon: f.geometry.coordinates[0], lat: f.geometry.coordinates[1], value: parseFloat(f.properties?.[resAttr]) }))
        .filter(p => !isNaN(p.value));
      const res = gridResiduals(points, gridFromResult(a.geojson).grid, gridFromResult(a.geojson).bbox);
      if (res.length === 0) throw new Error('No points fall inside the grid extent');
      let rmin = Infinity, rmax = -Infinity;
      res.forEach(r => { if (r.residual < rmin) rmin = r.residual; if (r.residual > rmax) rmax = r.residual; });
      const span = rmax > rmin ? rmax - rmin : 1;
      const geojson = {
        type: 'FeatureCollection',
        features: res.map(r => ({
          type: 'Feature',
          properties: {
            value: Math.round(r.residual * 1e4) / 1e4,
            observed: r.value, predicted: Math.round(r.predicted * 1e4) / 1e4,
            normalized: Math.round(((r.residual - rmin) / span) * 1e4) / 1e4,
          },
          geometry: { type: 'Point', coordinates: [r.lon, r.lat] },
        })),
        metadata: { min: rmin, max: rmax },
      };
      const rms = Math.sqrt(res.reduce((s, r) => s + r.residual ** 2, 0) / res.length);
      onAddResultLayer(geojson, `Residuals ${resAttr} vs ${a.name} (RMS ${rms.toPrecision(3)})`, 'grid');
      setSuccess(`✓ ${res.length} residuals · RMS ${rms.toPrecision(4)}`);
    } catch (err) { setError(err.message); }
  };

  const runBlanking = () => {
    setError(null); setSuccess(null);
    try {
      const a = usableGrids.find(l => l.id === gridA);
      if (!a) throw new Error('Select a grid layer');
      if (drawnPolygons.length === 0) throw new Error('Draw a polygon boundary first (Edit → Polygon)');
      const masked = blankGridOutside(gridFromResult(a.geojson), drawnPolygons);
      const geojson = gridToResultGeoJSON(masked.grid, masked.bbox);
      if (geojson.features.length === 0) throw new Error('All cells are outside the boundary');
      onAddResultLayer(geojson, `Blanked — ${a.name}`, 'grid');
      setSuccess(`✓ Blanked grid added (${geojson.features.length} cells kept)`);
    } catch (err) { setError(err.message); }
  };

  const selStyle = { width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' };
  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 };

  const gridSelect = (label, value, setValue) => (
    <div>
      <label style={lbl}>{label}</label>
      <select value={value} onChange={e => setValue(e.target.value)} style={selStyle}>
        <option value="">Select grid...</option>
        {usableGrids.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );

  const runBtn = (label, onClick, disabled = false) => (
    <button onClick={onClick} disabled={disabled}
      style={{ width: '100%', padding: '8px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: disabled ? 0.5 : 1 }}>
      {label}
    </button>
  );

  return (
    <div style={{ width: 260, background: C.bg2, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'auto', flexShrink: 0 }}>
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>🧮 Grid / Z Math</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>

      {/* Mode tabs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, padding: '8px 10px', borderBottom: `1px solid ${C.border}` }}>
        {MODES.map(m => (
          <button key={m.id} onClick={() => { setMode(m.id); setError(null); setSuccess(null); setVolResult(null); }}
            style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer', background: mode === m.id ? C.bg4 : 'transparent', color: mode === m.id ? C.accent : C.text2 }}>
            {m.label}
          </button>
        ))}
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* ── Grid ↔ grid math ── */}
        {mode === 'grids' && (
          <>
            {usableGrids.length < 2 ? (
              <div style={{ fontSize: 11, color: C.text3, lineHeight: 1.6 }}>
                Run Gridding at least twice first — grid math combines two gridding result layers.
              </div>
            ) : (
              <>
                {gridSelect('Grid A (Z1)', gridA, setGridA)}
                {gridSelect('Grid B (Z2)', gridB, setGridB)}
                <div>
                  <label style={lbl}>Operation</label>
                  <select value={gridOp} onChange={e => setGridOp(e.target.value)} style={selStyle}>
                    {OPERATIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
                {gridOp === 'formula' && (
                  <input value={formula} onChange={e => setFormula(e.target.value)}
                    placeholder="(Z1 - Z2) / Z1 * 100"
                    style={{ ...selStyle, fontFamily: 'monospace', boxSizing: 'border-box' }} />
                )}
                {runBtn('Combine Grids', runGridMath, !gridA || !gridB)}
              </>
            )}
          </>
        )}

        {/* ── Volume ── */}
        {mode === 'volume' && (
          <>
            {usableGrids.length === 0 ? (
              <div style={{ fontSize: 11, color: C.text3 }}>Run Gridding first to create a grid layer.</div>
            ) : (
              <>
                {gridSelect('Grid', gridA, setGridA)}
                <div>
                  <label style={lbl}>Datum (reference level)</label>
                  <input type="number" value={datum} onChange={e => setDatum(e.target.value)}
                    style={{ ...selStyle, boxSizing: 'border-box' }} />
                </div>
                {runBtn('Compute Volume', runVolume, !gridA)}
                {volResult && (
                  <div style={{ fontSize: 10, color: C.text1, fontFamily: 'monospace', lineHeight: 1.8, background: C.bg3, padding: '8px 10px', borderRadius: 6 }}>
                    Above datum: <span style={{ color: C.emerald }}>{volResult.above.toExponential(4)}</span><br />
                    Below datum: <span style={{ color: C.red }}>{volResult.below.toExponential(4)}</span><br />
                    Net: <span style={{ color: C.cyan }}>{volResult.net.toExponential(4)}</span><br />
                    <span style={{ color: C.text3 }}>(value-units × m² · {volResult.cells} cells)</span>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── Residuals ── */}
        {mode === 'residuals' && (
          <>
            {usableGrids.length === 0 || xyzLayers.length === 0 ? (
              <div style={{ fontSize: 11, color: C.text3, lineHeight: 1.6 }}>
                Needs an XYZ point layer and a gridding result. Residual = observed − grid prediction at each point.
              </div>
            ) : (
              <>
                <div>
                  <label style={lbl}>XYZ Layer (observed)</label>
                  <select value={resLayerId} onChange={e => { setResLayerId(e.target.value); setResAttr(''); }} style={selStyle}>
                    <option value="">Select...</option>
                    {xyzLayers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Value Attribute</label>
                  <select value={resAttr} onChange={e => setResAttr(e.target.value)} style={selStyle}>
                    <option value="">Select...</option>
                    {(xyzLayers.find(l => l.id === resLayerId)?.xyz?.zFields || []).map(z => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
                {gridSelect('Grid (predicted)', gridA, setGridA)}
                {runBtn('Compute Residuals', runResiduals, !gridA || !resLayerId || !resAttr)}
              </>
            )}
          </>
        )}

        {/* ── Blanking ── */}
        {mode === 'blanking' && (
          <>
            {usableGrids.length === 0 ? (
              <div style={{ fontSize: 11, color: C.text3 }}>Run Gridding first to create a grid layer.</div>
            ) : (
              <>
                {gridSelect('Grid to blank', gridA, setGridA)}
                <div style={{ fontSize: 10, color: drawnPolygons.length ? C.emerald : C.amber }}>
                  {drawnPolygons.length
                    ? `✓ ${drawnPolygons.length} drawn polygon${drawnPolygons.length > 1 ? 's' : ''} will be used as the boundary`
                    : '⚠ Draw a boundary polygon first (Edit → Polygon)'}
                </div>
                {runBtn('Blank Outside Boundary', runBlanking, !gridA || drawnPolygons.length === 0)}
              </>
            )}
          </>
        )}

        {mode !== 'zfields' && error && <div style={{ fontSize: 10, color: C.red, padding: '5px 8px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>✗ {error}</div>}
        {mode !== 'zfields' && success && <div style={{ fontSize: 10, color: C.emerald, padding: '5px 8px', background: 'rgba(52,211,153,0.1)', borderRadius: 6 }}>{success}</div>}

        {/* ── Z-field math (original mode) ── */}
        {mode === 'zfields' && (xyzLayers.length === 0 ? (
          <div style={{ fontSize: 11, color: C.text3, lineHeight: 1.6 }}>
            Import an XYZ dataset with at least two value columns first (Insert → Import File).
          </div>
        ) : (
          <>
            <div>
              <label style={lbl}>XYZ Layer</label>
              <select value={layerId} onChange={e => { setLayerId(e.target.value); setZ1(''); setZ2(''); }} style={selStyle}>
                {xyzLayers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Z1</label>
                <select value={z1} onChange={e => setZ1(e.target.value)} style={selStyle}>
                  <option value="">Select...</option>
                  {zFields.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Z2</label>
                <select value={z2} onChange={e => setZ2(e.target.value)} style={selStyle}>
                  <option value="">Select...</option>
                  {zFields.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={lbl}>Operation</label>
              <select value={op} onChange={e => setOp(e.target.value)} style={selStyle}>
                {OPERATIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            {op === 'formula' && (
              <div>
                <label style={lbl}>Formula (use Z1 and Z2)</label>
                <input value={formula} onChange={e => setFormula(e.target.value)}
                  placeholder="(Z1 - Z2) / Z1 * 100"
                  style={{ ...selStyle, fontFamily: 'monospace', boxSizing: 'border-box' }} />
                <div style={{ fontSize: 9, color: C.text3, marginTop: 3 }}>
                  Supported: + − × ÷ ( ) abs sqrt log log10 exp pow min max
                </div>
              </div>
            )}
            <div>
              <label style={lbl}>Output Field Name</label>
              <input value={outName} onChange={e => setOutName(e.target.value)}
                placeholder={op === 'formula' ? 'derived' : z1 && z2 ? `${z1}_${op}_${z2}` : 'auto'}
                style={{ ...selStyle, boxSizing: 'border-box' }} />
            </div>

            {error && <div style={{ fontSize: 10, color: C.red, padding: '5px 8px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>✗ {error}</div>}
            {success && <div style={{ fontSize: 10, color: C.emerald, padding: '5px 8px', background: 'rgba(52,211,153,0.1)', borderRadius: 6 }}>{success}</div>}

            <button onClick={run} disabled={!z1 || !z2}
              style={{ width: '100%', padding: '8px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: (!z1 || !z2) ? 0.5 : 1 }}>
              Compute Derived Field
            </button>
          </>
        ))}
      </div>
    </div>
  );
}
